'use client';

import { useLanguage } from '@/lib/i18n/LanguageContext';
import { BUTTON_COLOR_STYLES, isSafeUrl, linkHost, type MessageSpan, type RegistrationMessage } from '@/lib/registration/message';

interface RegistrationMessageCardProps {
  message: RegistrationMessage;
  organizerName?: string | null;
  className?: string;
}

/**
 * The organizer's message after registration, framed as theirs. Rendered from
 * the stored blocks with React's escaping – never as HTML – and every link is
 * re-checked here, whatever the database holds.
 */
export default function RegistrationMessageCard({ message, organizerName, className = '' }: RegistrationMessageCardProps) {
  const { t } = useLanguage();
  const name = organizerName?.trim();
  return (
    <section
      aria-label={t('registrationMessage.fromOrganizer')}
      className={`rounded-lg border border-gray-200 bg-white p-4 ${className}`}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {name ? t('registrationMessage.fromOrganizerNamed', { name }) : t('registrationMessage.fromOrganizer')}
      </p>
      <RegistrationMessageContent message={message} />
    </section>
  );
}

/** The message itself – paragraphs and the button – without a frame (the pop-up and the card both use it). */
export function RegistrationMessageContent({ message }: { message: RegistrationMessage }) {
  return (
    <div className="space-y-2 text-sm leading-relaxed text-gray-800">
      {message.blocks.map((block, i) => {
        if (block.type === 'paragraph') {
          return (
            <p key={i} className="whitespace-pre-line break-words">
              {block.content.length === 0 ? ' ' : block.content.map((span, j) => <Span key={j} span={span} />)}
            </p>
          );
        }
        if (!isSafeUrl(block.url)) return null;
        const { bg, fg } = BUTTON_COLOR_STYLES[block.color];
        return (
          <div key={i} className="pt-2 text-center">
            <a
              href={block.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-block max-w-full break-words rounded-md px-5 py-2.5 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              style={{ backgroundColor: bg, color: fg }}
            >
              {block.label}
            </a>
            <p className="mt-1 text-xs text-gray-500">{linkHost(block.url)}</p>
          </div>
        );
      })}
    </div>
  );
}

function Span({ span }: { span: MessageSpan }) {
  let node: React.ReactNode = span.text;
  if (span.bold) node = <strong>{node}</strong>;
  if (span.italic) node = <em>{node}</em>;
  if (span.href && isSafeUrl(span.href)) {
    node = (
      <a href={span.href} target="_blank" rel="noopener noreferrer nofollow" className="text-blue-600 underline hover:text-blue-800">
        {node}
      </a>
    );
  }
  return <>{node}</>;
}
